import React from 'react';
import { VoiceAndToneMatchType } from 'src/components/BeaconRedesignComponents/ExperimentalLayout/ContentV2/ContentServerProxy/types';
import ScatterPlot, {
  ScatterPlotDatum
} from 'src/components/BeaconRedesignComponents/ExperimentalLayout/ContentV2/VoiceAndTone/ScatterPlot';
import { Card } from 'src/components/BeaconRedesignComponents/ExperimentalLayout/ContentV2/VoiceAndTone/ScatterPlotCard';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import BeaconProChart from 'src/components/BeaconRedesignComponents/ExperimentalLayout/ContentV2/Summary/Visualizations/BeaconProChart';
import {
  ChartDataAdapter,
  VoiceAndToneMatchAnalysisRow
} from 'src/components/BeaconRedesignComponents/ExperimentalLayout/ContentV2/Summary/Visualizations/adapters/types';
import { getProductImageUrl } from 'src/utils/image';
import { formatMetric } from 'src/utils/app';
import { METRICTYPE } from 'src/utils/entityDefinitions';
import { useBeaconNavigator } from 'src/components/BeaconV2Components/BeaconNavigation/useBeaconNavigator';
import { BEACON_SUBTABS, BEACON_TABS } from 'src/components/BeaconV2Components/GenericSidebarNav/useBeaconRoutes';

const badgeVariantMap = {
  [VoiceAndToneMatchType.VoiceMismatch]: {
    text: 'Voice Mismatch',
    backgroundColor: '#FDF2EA',
    color: '#ED792F'
  },
  [VoiceAndToneMatchType.FullMatch]: {
    text: 'Full Match',
    backgroundColor: '#F1F9F3',
    color: '#71C184'
  },
  [VoiceAndToneMatchType.FullMismatch]: {
    text: 'Full Mismatch',
    backgroundColor: '#F6E8F1',
    color: '#A21671'
  },
  [VoiceAndToneMatchType.ToneMismatch]: {
    text: 'Tone Mismatch',
    backgroundColor: '#FFEEED',
    color: '#FF534A'
  }
};

// TODO: Extend this interface with data used to populate the scatter plot product cards
export interface VoiceAndToneDatum extends ScatterPlotDatum {
  matchType: VoiceAndToneMatchType;
}
interface VoiceAndToneScatterPlotProps {
  title: string;
  queryFn: () => Promise<any>;
  renderSubtitle: () => string | React.ReactNode;
  renderTitle?: () => string | React.ReactNode;
}
/**
 * @deprecated
 * For backward compatibility as ContentV2 is already in use in production
 * This component will be removed in ContentV3
 */
export const VoiceAndToneScatterPlot = (props: VoiceAndToneScatterPlotProps) => {
  const { title, queryFn, renderSubtitle, renderTitle } = props;
  const { beaconNavigator } = useBeaconNavigator();

  return (
    <BeaconProChart name={`voice-and-tone-scatter-plot-${title.toLowerCase().replace(/\s+/g, '-')}`} queryFn={queryFn}>
      <BeaconProChart.Header menuButton={null}>
        <BeaconProChart.Title>
          {renderTitle && renderTitle()}
          {!renderTitle && <Text variant="h4">{title}</Text>}
        </BeaconProChart.Title>
      </BeaconProChart.Header>
      <BeaconProChart.Subtitle render={renderSubtitle} />

      <BeaconProChart.Visualization
        render={(data: ChartDataAdapter<(VoiceAndToneMatchAnalysisRow & VoiceAndToneDatum)[]>, isLoading: boolean) => {
          if (isLoading) {
            return null;
          }

          const tableGridData = data.transform();

          return (
            <div style={{ marginTop: '32px' }}>
              <ScatterPlot
                yAxisLabel="Tone"
                xAxisLabel="Voice"
                data={tableGridData}
                height={517}
                renderTooltip={(point: VoiceAndToneMatchAnalysisRow & VoiceAndToneDatum) => {
                  const badgeProps = badgeVariantMap[point?.matchType];

                  return (
                    <Card>
                      <Card.Badge {...badgeProps} />
                      <Card.Product
                        name={point.product.brandName}
                        description={point.product.title}
                        image={getProductImageUrl(point.product.stacklineSku)}
                      />
                      <Card.MetricsSection>
                        <Card.MetricsColumn>
                          <Card.Metric
                            label="Voice Score"
                            value={`${formatMetric(point.voiceMatchScore.value, METRICTYPE.PERCENT, {
                              decimalPlaces: 1
                            })}`}
                            percentChange={point.voiceMatchScore.difference}
                            percentChangeFormatted={`${formatMetric(
                              point.voiceMatchScore.difference,
                              METRICTYPE.PERCENT,
                              {
                                decimalPlaces: 1
                              }
                            )}`}
                          />
                          <Card.MetricInfo label="Current" value={point.voice.actual} />
                          <Card.MetricInfo label="Target" value={point.voice.expected} />
                        </Card.MetricsColumn>
                        <Card.Divider />
                        <Card.MetricsColumn>
                          <Card.Metric
                            label="Tone Score"
                            value={`${formatMetric(point.toneMatchScore.value, METRICTYPE.PERCENT, {
                              decimalPlaces: 1
                            })}`}
                            percentChange={point.toneMatchScore.difference}
                            percentChangeFormatted={`${formatMetric(
                              point.toneMatchScore.difference,
                              METRICTYPE.PERCENT,
                              {
                                decimalPlaces: 1
                              }
                            )}`}
                          />
                          <Card.MetricInfo label="Current" value={point.tone.actual} />
                          <Card.MetricInfo label="Target" value={point.tone.expected} />
                        </Card.MetricsColumn>
                      </Card.MetricsSection>
                      <Card.ActionButton
                        text="View details"
                        onClick={() => {
                          beaconNavigator.setPathname(`/product/${point.product.stacklineSku}`);
                          beaconNavigator.updateSearchParams({
                            tab: BEACON_TABS.CONTENT_V2,
                            subtab: BEACON_SUBTABS.CONTENT_VOICE_AND_TONE
                          });

                          beaconNavigator.go();
                        }}
                      />
                    </Card>
                  );
                }}
              />
            </div>
          );
        }}
      />
    </BeaconProChart>
  );
};
